import React, { useState } from 'react';
import { compose, lifecycle } from 'recompose';
import { connect } from '../../../redux/store';
import { actions as portfolioActions } from '../../../redux/modules/settingsPortfolio';
import { actions as serviceActions } from '../../../redux/modules/settingsService';
import { actions as appendixActions } from '../../../redux/modules/settingsAppendix';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import { PageTitle } from '../../shared/PageTitle';
import {
  NewPortfolioModal,
  NewServiceModal,
  NewAppendixModal,
} from './NewModals';
// import { CategorySyncAlert } from './CategorySyncAlert';
import {
  LoadingMessage,
  EmptyMessage,
  ErrorMessage,
  TimeAgo,
  C,
} from '@kineticdata/bundle-common';
import { Map } from 'immutable';
import classNames from 'classnames';

const CatalogManagementComponent = ({
  type,
  // Portoflios
  portfolios,
  portfoliosList,
  portfoliosError,
  portfoliosInit,
  portfoliosLoading,
  portfoliosNameMap,
  portfolioActionsPending,
  // Services
  services,
  servicesList,
  servicesError,
  servicesInit,
  servicesLoading,
  serviceActionsPending,
  // Appendices
  appendices,
  appendicesList,
  appendicesError,
  appendicesInit,
  appendicesLoading,
  appendixActionsPending,
  // Actions
  fetchPortfolios,
  createPortfolio,
  fetchServices,
  createService,
  fetchAppendices,
  createAppendix,
  // Other state
  profile,
  appLocation,
  isMobile,
  navigate,
}) => {
  // State for managing visibility of modal for creating new entities
  const [newModalOpen, setNewModalOpen] = useState(false);

  return ['portfolios', 'services', 'appendices'].includes(type) ? (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          parts={['Catalog Management']}
          title="Catalog Management"
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
          ]}
          actions={[
            {
              icon:
                !portfoliosLoading && !servicesLoading /*&& !appendicesLoading*/
                  ? 'sync'
                  : 'sync fa-spin',
              onClick: () => {
                fetchPortfolios();
                fetchServices();
                fetchAppendices();
              },
            },
            profile.isSMO && {
              label: 'Create New Portfolio',
              onClick: () => setNewModalOpen('portfolio'),
              disabled: portfolioActionsPending || newModalOpen === 'portfolio',
              menu: true,
            },
            profile.isSMO && {
              label: 'Create New Service',
              onClick: () => setNewModalOpen('service'),
              disabled: serviceActionsPending || newModalOpen === 'service',
              menu: true,
            },
            profile.isSMO && {
              label: 'Create New Appendix',
              onClick: () => setNewModalOpen('appendix'),
              disabled: appendixActionsPending || newModalOpen === 'appendix',
              menu: true,
            },
            profile.isSMO && {
              label: 'Manage External Offerings',
              to: `/settings/adminforms/${
                C.FORM_SLUG_EXTERNAL_SERVICE_OFFERINGS
              }`,
              menu: true,
              divider: true,
            },
            profile.isSMO && {
              label: 'Manage Rate Cards',
              to: `/settings/adminforms/${C.FORM_SLUG_RATE_CARD}`,
              menu: true,
            },
            (profile.isSMO || profile.isCatalogManager) && {
              label: 'View Catalog Ownership',
              to: `${appLocation}/settings/catalog/ownership`,
              menu: true,
            },
          ]}
        />

        {newModalOpen === 'portfolio' && (
          <NewPortfolioModal
            toggle={() => setNewModalOpen(false)}
            createPortfolio={createPortfolio}
            onSuccess={submission =>
              navigate(
                `../portfolios/${submission.values[C.FIELD_PORTFOLIO_ID]}`,
              )
            }
          />
        )}

        {newModalOpen === 'service' && (
          <NewServiceModal
            toggle={() => setNewModalOpen(false)}
            createService={createService}
            onSuccess={submission =>
              navigate(`../services/${submission.values[C.FIELD_SERVICE_ID]}`)
            }
            portfoliosList={portfoliosList}
          />
        )}

        {newModalOpen === 'appendix' && (
          <NewAppendixModal
            toggle={() => setNewModalOpen(false)}
            createAppendix={createAppendix}
            onSuccess={submission =>
              navigate(
                `../appendices/${submission.values[C.FIELD_APPENDIX_ID]}`,
              )
            }
          />
        )}

        {/*<CategorySyncAlert />*/}

        <ul className="nav nav-tabs h6" role="tablist">
          <li role="tab" className="nav-item">
            <Link
              to="../portfolios"
              className={classNames('nav-link', {
                active: type === 'portfolios',
              })}
            >
              <I18n>Portfolios</I18n>
            </Link>
          </li>
          <li role="tab" className="nav-item">
            <Link
              to="../services"
              className={classNames('nav-link', {
                active: type === 'services',
              })}
            >
              <I18n>Services</I18n>
            </Link>
          </li>
          <li role="tab" className="nav-item">
            <Link
              to="../appendices"
              className={classNames('nav-link', {
                active: type === 'appendices',
              })}
            >
              <I18n>Appendices</I18n>
            </Link>
          </li>
        </ul>

        {type === 'portfolios' &&
          (portfoliosError ? (
            <ErrorMessage
              title="Failed to retrieve portfolios."
              message={portfoliosError.message}
            />
          ) : !portfoliosInit && portfoliosLoading ? (
            <LoadingMessage />
          ) : portfoliosList.size === 0 ? (
            <EmptyMessage title="There are no portfolios." />
          ) : (
            <div className="scroll-wrapper-h">
              <table className="table table--settings">
                <thead>
                  <tr>
                    <th>
                      <I18n>Portfolio Name</I18n>
                    </th>
                    <th>
                      <I18n>Status</I18n>
                    </th>
                    <th>
                      <I18n>Type</I18n>
                    </th>
                    <th>
                      <I18n>Sort</I18n>
                    </th>
                    <th>
                      <I18n>Last Updated</I18n>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {portfoliosList.map(portfolio => {
                    return (
                      <tr key={portfolio.id}>
                        <td>
                          <Link to={portfolio.values[C.FIELD_PORTFOLIO_ID]}>
                            {portfolio.values['Name']}
                          </Link>
                        </td>
                        <td>
                          {portfolio.values['Status']}
                          {portfolio.values['Status'] === 'Active' &&
                          !!portfolios
                            .get(portfolio.values[C.FIELD_PORTFOLIO_ID])
                            .find(p => p.values['Status'] !== 'Active') ? (
                            <>
                              {' \u00B7 '}
                              <I18n>In Revision</I18n>
                            </>
                          ) : null}
                        </td>
                        <td>{portfolio.values['Type']}</td>
                        <td>{portfolio.values['Sort Priority']}</td>
                        <td>{<TimeAgo timestamp={portfolio.updatedAt} />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

        {type === 'services' &&
          (servicesError ? (
            <ErrorMessage
              title="Failed to retrieve services."
              message={servicesError.message}
            />
          ) : !servicesInit && servicesLoading ? (
            <LoadingMessage />
          ) : servicesList.size === 0 ? (
            <EmptyMessage title="There are no services." />
          ) : (
            <div className="scroll-wrapper-h">
              <table className="table table--settings">
                <thead>
                  <tr>
                    <th>
                      <I18n>Service Name</I18n>
                    </th>
                    <th>
                      <I18n>Status</I18n>
                    </th>
                    <th>
                      <I18n>Type</I18n>
                    </th>
                    <th>
                      <I18n>Sort</I18n>
                    </th>
                    <th>
                      <I18n>Portfolios</I18n>
                    </th>
                    <th>
                      <I18n>Last Updated</I18n>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {servicesList.map(service => {
                    return (
                      <tr key={service.id}>
                        <td>
                          <Link to={service.values[C.FIELD_SERVICE_ID]}>
                            {service.values['Name']}
                          </Link>
                        </td>
                        <td>
                          {service.values['Status']}
                          {service.values['Status'] === 'Active' &&
                          !!services
                            .get(service.values[C.FIELD_SERVICE_ID])
                            .find(p => p.values['Status'] !== 'Active') ? (
                            <>
                              {' \u00B7 '}
                              <I18n>In Revision</I18n>
                            </>
                          ) : null}
                        </td>
                        <td>{service.values['Type']}</td>
                        <td>{service.values['Sort Priority']}</td>
                        <td className="multi">
                          {(service.values[C.FIELD_PORTFOLIO_ID] || []).map(
                            pId =>
                              portfoliosNameMap.has(pId) ? (
                                <span key={pId}>
                                  <Link to={`../portfolios/${pId}`}>
                                    <I18n>
                                      {portfoliosNameMap.get(pId) || pId}
                                    </I18n>
                                  </Link>
                                </span>
                              ) : null,
                          )}
                        </td>
                        <td>{<TimeAgo timestamp={service.updatedAt} />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

        {type === 'appendices' &&
          (appendicesError ? (
            <ErrorMessage
              title="Failed to retrieve appendices."
              message={appendicesError.message}
            />
          ) : !appendicesInit && appendicesLoading ? (
            <LoadingMessage />
          ) : appendicesList.size === 0 ? (
            <EmptyMessage title="There are no appendices." />
          ) : (
            <div className="scroll-wrapper-h">
              <table className="table table--settings">
                <thead>
                  <tr>
                    <th>
                      <I18n>Appendix Name</I18n>
                    </th>
                    <th>
                      <I18n>Status</I18n>
                    </th>
                    <th>
                      <I18n>Type</I18n>
                    </th>
                    <th>
                      <I18n>Last Updated</I18n>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {appendicesList.map(appendix => {
                    return (
                      <tr key={appendix.id}>
                        <td>
                          <Link to={appendix.values[C.FIELD_APPENDIX_ID]}>
                            {appendix.values['Name']}
                          </Link>
                        </td>
                        <td>
                          {appendix.values['Status']}
                          {appendix.values['Status'] === 'Active' &&
                          !!appendices
                            .get(appendix.values[C.FIELD_APPENDIX_ID])
                            .find(p => p.values['Status'] !== 'Active') ? (
                            <>
                              {' \u00B7 '}
                              <I18n>In Revision</I18n>
                            </>
                          ) : null}
                        </td>
                        <td>{appendix.values['Type']}</td>
                        <td>{<TimeAgo timestamp={appendix.updatedAt} />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </div>
    </div>
  ) : (
    <ErrorMessage title="Page not found" />
  );
};

const mapStateToProps = (state, props) => {
  return {
    // Portfolios
    portfolios: state.settingsPortfolio.data,
    portfoliosList: state.settingsPortfolio.dataList,
    portfoliosInit: state.settingsPortfolio.dataInit,
    portfoliosLoading: state.settingsPortfolio.dataLoading,
    portfoliosError: state.settingsPortfolio.dataError,
    portfoliosNameMap: state.settingsPortfolio.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
    // Portfolio actions pending
    portfolioActionsPending: state.settingsPortfolio.actionsPending > 0,
    // Services
    services: state.settingsService.data,
    servicesList: state.settingsService.dataList,
    servicesInit: state.settingsService.dataInit,
    servicesLoading: state.settingsService.dataLoading,
    servicesError: state.settingsService.dataError,
    // Services actions pending
    serviceActionsPending: state.settingsService.actionsPending > 0,
    // Appendices
    appendices: state.settingsAppendix.data,
    appendicesList: state.settingsAppendix.dataList,
    appendicesInit: state.settingsAppendix.dataInit,
    appendicesLoading: state.settingsAppendix.dataLoading,
    appendicesError: state.settingsAppendix.dataError,
    // Appendices actions pending
    appendixActionsPending: state.settingsAppendix.actionsPending > 0,
    // Other state
    profile: state.app.profile,
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchPortfolios: portfolioActions.fetchPortfoliosRequest,
  fetchServices: serviceActions.fetchServicesRequest,
  fetchAppendices: appendixActions.fetchAppendicesRequest,
  createPortfolio: portfolioActions.createPortfolioRequest,
  createService: serviceActions.createServiceRequest,
  createAppendix: appendixActions.createAppendixRequest,
};

export const CatalogManagement = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CatalogManagementComponent);

/**
 * Wrapper that fetches the full lists for portfolios, services, and appendices
 * on mount for any of the catalog management pages.
 */
export const CatalogManagementWrapper = compose(
  connect(
    (state, props) => {
      const profile = state.app.profile || {};
      return {
        hasAccess: profile.isSMO || profile.isCatalogManager,
        fetchSubteams: state.app.actions.fetchSubteams,
      };
    },
    {
      fetchPortfolios: portfolioActions.fetchPortfoliosRequest,
      fetchServices: serviceActions.fetchServicesRequest,
      fetchCategories: serviceActions.fetchCategoriesRequest,
      fetchAppendices: appendixActions.fetchAppendicesRequest,
    },
  ),
  lifecycle({
    componentDidMount() {
      this.props.fetchPortfolios();
      this.props.fetchServices();
      this.props.fetchCategories();
      this.props.fetchAppendices();
      this.props.fetchSubteams({
        teamName: C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME,
      });
      this.props.fetchSubteams({
        teamName: C.CATALOG_BUSINESS_OWNERS_TEAM_NAME,
      });
    },
  }),
)(
  props =>
    props.hasAccess ? (
      props.children
    ) : (
      <div className="page-container">
        <div className="page-panel">
          <PageTitle
            parts={['Catalog Management']}
            title="Catalog Management"
            breadcrumbs={[
              {
                label: (
                  <span>
                    <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                    <I18n>Settings</I18n>
                  </span>
                ),
                to: '/settings',
              },
            ]}
          />
          <ErrorMessage
            title="Unauthorized"
            message="You do not have permission to manage the catalog."
          />
        </div>
      </div>
    ),
);
